import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";

export const useHashAuthentication = () => {
  const [processingHash, setProcessingHash] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Extract hash and query parameters from the URL
    const hash = window.location.hash;
    const params = new URLSearchParams(window.location.search);
    
    // Parse hash parameters (Supabase sometimes puts auth tokens in hash)
    const hashParams = new URLSearchParams(hash.replace('#', ''));
    
    // Get relevant authentication parameters
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const type = hashParams.get('type');
    const recoveryType = params.get('type');
    const errorCode = params.get('error');
    const errorDescription = params.get('error_description');
    
    // Check if there are any auth-related parameters in the URL
    const hasAuthParams = 
      (accessToken && refreshToken) || 
      hash.includes('access_token') || 
      window.location.search.includes('error=') ||
      recoveryType === 'recovery' ||
      window.location.search.includes('token=');
      
    console.log('URL auth parameters check:', { 
      hasHash: !!hash, 
      hasAuthParams, 
      type,
      recoveryType,
      hasError: !!errorCode
    });
    
    if (hasAuthParams) {
      setProcessingHash(true);
      
      // Handle error cases
      if (errorCode) {
        console.error('Auth error in URL:', errorCode, errorDescription);
        toast.error(errorDescription || 'Authentication error');
        setProcessingHash(false);
        // Clean up error params from URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      // Handle recovery token in access_token/refresh_token format (hash fragment)
      if (accessToken && refreshToken && type === 'recovery') {
        console.log('Found recovery tokens in URL hash, setting session...');
        
        // Store the password reset state in localStorage
        localStorage.setItem('passwordResetRequired', 'true');
        console.log('Set passwordResetRequired flag in localStorage (hash auth)');
        
        supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        }).then(({ data, error }) => {
          if (error) {
            console.error('Error setting session from tokens:', error);
            toast.error('Failed to authenticate with provided tokens.');
            setProcessingHash(false);
          } else if (data?.session) {
            console.log('Session set successfully from tokens:', data.session.user.id);
            toast.success('Authentication successful!');
            window.location.href = '/settings';
          } else {
            console.error('No session returned from setSession');
            setProcessingHash(false);
          }
        });
        
        // Keep the 'type=recovery' parameter in the URL but clean up the hash
        const cleanUrl = `${window.location.pathname}?type=recovery`;
        window.history.replaceState({}, document.title, cleanUrl);
        return;
      }
      
      // Let Supabase auth client handle token processing
      console.log('Letting Supabase process auth parameters...');
      const timer = setTimeout(() => {
        supabase.auth.getSession().then(({ data }) => {
          if (data?.session) {
            console.log("Session established after auth parameter processing:", data.session.user.id);
            
            // If this is a recovery flow, keep the user on the auth page to reset password
            if (recoveryType === 'recovery' || type === 'recovery') {
              console.log("Recovery flow detected, keeping user on auth page");
              
              // Store the password reset state in localStorage
              localStorage.setItem('passwordResetRequired', 'true');
              console.log('Set passwordResetRequired flag in localStorage (recovery flow)');
              
              const cleanUrl = `${window.location.pathname}?type=recovery`;
              window.history.replaceState({}, document.title, cleanUrl);
              setProcessingHash(false);
            } else {
              window.location.href = '/settings';
            }
          } else {
            console.log("No session established after processing URL parameters");
            setProcessingHash(false);
          }
        });
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [location]);

  return { processingHash };
};
